import * as session from './customer-api/session';
import * as enhancedconversions from './enhanced-conversions';
import * as gtm from './gtm';
import * as pinterest from './pinterest';
import * as reddit from './reddit';
import { GetFirstAndLast } from './users';

declare interface purchaseTrackingWindow extends Window {
    dataLayer?: Record<string, unknown>[];
}
declare const window: purchaseTrackingWindow;

export function TrackPurchase(opts: {
    invoiceNumber: string;
    orderID: string;
    value: number;
    cartItems: { id?: string; quantity?: number }[];
}): void {
    const numItems = opts.cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

    try {
        reddit.TrackPurchase({
            value: opts.value,
            currency: 'USD',
            transactionId: opts.invoiceNumber,
            itemCount: numItems,
            eventId: opts.invoiceNumber,
        });
    } catch (e) {
        console.error('reddit tracking error', e);
    }

    try {
        pinterest.TrackCheckout({
            value: opts.value,
            currency: 'USD',
            orderQuantity: numItems,
            eventId: opts.invoiceNumber,
        });
    } catch (e) {
        console.error('pinterest tracking error', e);
    }

    try {
        gtm.TrackPurchase({
            orderID: opts.orderID,
            invoiceNumber: opts.invoiceNumber,
            value: opts.value,
            numItems: numItems,
        });
    } catch (e) {
        console.error('gtm tracking error', e);
    }
}

// TrackCheckoutSuccess pushes the `submit-success-click` conversion event
// after every charge on a new order succeeded. Call it ONLY on that path —
// never on a decline — and before any PayPal redirect.
//
// The user data describes ONE person. Logged in, that's the account holder
// (whose email/phone go out); when the shipping recipient's name isn't theirs
// (a gift), the recipient's address belongs to someone else and is withheld
// rather than sent as a mixed identity. Guests are the recipient themselves.
// The event carries enhancedconversions.Set()'s gated view — Google's
// user-provided-data address matching is all-or-nothing, so a partial
// address (a one-word recipient name, a withheld ZIP) is dropped rather
// than sent incomplete (#9198 class 3).
export async function TrackCheckoutSuccess(opts: {
    // The shipping recipient as entered at checkout. email/phone are only
    // used for guests — a logged-in customer's come from the session.
    shipping: {
        fullName: string;
        email: string;
        // Lazy: formatting the phone input is async (intl-tel-input utils)
        // and only needed for guests.
        phone: () => Promise<string>;
        addressLine: string;
        city: string;
        state: string;
        zip: string;
        // ISO 3166-1 alpha-2 — Google requires it.
        country: string;
    };
    transactionId: string;
    products: string;
    total: string;
}): Promise<void> {
    const recipient = GetFirstAndLast(opts.shipping.fullName);

    let email: string;
    let phone: string;
    let firstName = recipient.firstName;
    let lastName = recipient.lastName;
    let addressLine = opts.shipping.addressLine;
    let city = opts.shipping.city;
    let state = opts.shipping.state;
    let zip = opts.shipping.zip;
    let country = opts.shipping.country;

    if (session.GetLoginToken()) {
        const user = (await session.Session()).user;
        email = user.email;
        phone = user.phone;
        firstName = user.firstName ?? '';
        lastName = user.lastName ?? '';
        if (recipient.firstName.toLowerCase() !== firstName.toLowerCase() ||
            recipient.lastName.toLowerCase() !== lastName.toLowerCase()) {
            addressLine = '';
            city = '';
            state = '';
            zip = '';
            country = '';
        }
    } else {
        email = opts.shipping.email;
        phone = await opts.shipping.phone();
    }

    const userData = enhancedconversions.Set({
        email,
        phone,
        firstName,
        lastName,
        addressLine,
        city,
        state,
        zip,
        country,
    });
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
        'event': 'submit-success-click',
        'userData': { ...userData },
        'transactionId': opts.transactionId,
        'products': opts.products,
        'total': opts.total,
    });
}
